import { logger } from 'common/services';
import { addAppErrorMessage } from 'modules/alerts/state';

import { openNatterFeature } from '../OpenNatterFeature';
import {
  socketConnected,
  socketDisconnected,
  socketReconnected,
  subscribeToOpenNatter,
} from '../state/openNatter.actions';
import { dispatchOpenNatterMessage } from './openNatterMessageDispatcher';
import { SocketEvents } from './OpenNatterSocket';
import { sequenceChecker } from './sequenceChecker';

import type { OpenNatterMessage } from '../types/messages';
import type { Middleware } from '@reduxjs/toolkit';

/**
 * Open Natter socket middleware
 * Connects the WebSocket to Redux, dispatching messages as actions
 * Socket listeners are set up when subscribeToOpenNatter is dispatched (the join flow)
 */
export const openNatterSocketMiddleware: Middleware = (store) => {
  let socketListenersSetUp = false;

  const setupSocketListeners = () => {
    if (socketListenersSetUp) return;

    const socket = openNatterFeature.getSocket();

    // Listen for WebSocket connection events
    socket.on(SocketEvents.connected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket connected');
      store.dispatch(socketConnected());
    });

    socket.on(SocketEvents.reconnected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket reconnected');
      // socketConnected keeps the slice's connection status correct; socketReconnected
      // triggers the re-join (only on reconnect, never on the initial connect).
      store.dispatch(socketConnected());
      store.dispatch(socketReconnected());
    });

    socket.on(SocketEvents.disconnected, () => {
      logger.info('[OpenNatterSocketMiddleware] Socket disconnected');
      store.dispatch(socketDisconnected());
    });

    // Listen for WebSocket messages and dispatch them to Redux
    socket.on('message', ({ destination, payload }: { destination: string; payload: OpenNatterMessage }) => {
      logger.debug('[OpenNatterSocketMiddleware] Received message', {
        destination,
        messageType: payload.type,
      });

      try {
        // Process message through sequence checker
        // This ensures messages are dispatched in order
        const messagesToDispatch = sequenceChecker.processMessage(payload);

        // Dispatch all messages (in order)
        for (const message of messagesToDispatch) {
          dispatchOpenNatterMessage(store.dispatch, message);
        }
      } catch (error) {
        logger.error('[OpenNatterSocketMiddleware] Error processing message', {
          error,
          destination,
          messageType: payload.type,
        });
      }
    });

    // Listen for socket errors (Ably `failed`/`suspended` — e.g. permanent auth failure). Surface a
    // user-facing message rather than only logging, so a dropped connection isn't silent.
    socket.on('error', (error: Error) => {
      logger.error('[OpenNatterSocketMiddleware] Socket error', { error });
      store.dispatch(addAppErrorMessage('Lost connection to Open Natter. Please refresh to reconnect.'));
    });

    socketListenersSetUp = true;
  };

  return (next) => (action) => {
    // Set up socket listeners when connecting or subscribing to Open Natter
    if (subscribeToOpenNatter.match(action)) {
      setupSocketListeners();
    }
    return next(action);
  };
};
